import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import StripeSecurityNote from "@/components/StripeSecurityNote";

const inp = "mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30 bg-white";

export default function QuoteView() {
  const { num } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/quotes/${num}`).then((r) => setQ(r.data)).catch((e) => setErr(apiErr(e))); }, [num]);

  const minDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }, []);

  const loadSlots = async (d) => {
    setDate(d); setTime(""); setSlots([]);
    try {
      const dur = q.quote_duration_minutes || 240;
      const { data } = await api.get("/availability", { params: { date: d, service_slug: "airbnb", tier_id: "abr1" } });
      // availability computes slots for its own duration; for quotes we still show business slots
      setSlots(data.slots);
    } catch { setSlots([]); }
  };

  const accept = async () => {
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${q.estimate_id}/accept`, { date, start_time: time });
      const pay = await api.post("/payments/checkout", { booking_id: data.booking_id, kind: "deposit", origin_url: window.location.origin });
      window.location.href = pay.data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  if (err) return <div className="max-w-lg mx-auto px-4 py-20 text-center"><h1 className="font-serif text-3xl text-[#2C2621]">Quote not found</h1><p className="mt-2 text-[#635B52]">{err}</p></div>;
  if (!q) return <div className="py-20 text-center text-[#635B52]">Loading…</div>;

  const canAccept = q.status === "quote_sent" && !q.expired && q.final_price;

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-14">
      <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Your quote</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2C2621]">{q.estimate_number}</h1>
      <div className="mt-6 bg-white rounded-2xl border border-[#E6DEC3] p-6 shadow-[0_4px_20px_rgba(44,38,33,0.04)]" data-testid="quote-card">
        <p className="text-[#635B52]">{q.requested_service} · {q.property_type} · {q.bedrooms} bd / {q.bathrooms} ba</p>
        {q.final_price ? (
          <div className="mt-3">
            <p className="font-serif text-4xl text-[#2C2621]" data-testid="quote-price">${Number(q.quote_total ?? q.final_price).toFixed(2)}</p>
            {q.quote_total != null && <div className="mt-3"><PriceBreakdown data={{ subtotal: q.quote_subtotal, tax_amount: q.quote_tax_amount, tax_rate: q.quote_tax_rate, total_price: q.quote_total, deposit_amount: q.quote_deposit, balance_amount: q.quote_balance, tax_label: q.quote_tax_label }} /></div>}
          </div>
        ) : <p className="mt-3 text-[#B87333]">Your quote is being prepared. We'll email you when it's ready.</p>}
        {q.included_services?.length > 0 && <ul className="mt-3 text-sm text-[#635B52] list-disc pl-5">{q.included_services.map((s, i) => <li key={i}>{s}</li>)}</ul>}
        {q.owner_note && <p className="mt-3 text-sm text-[#635B52]">{q.owner_note}</p>}
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className={`px-2.5 py-1 rounded-full ${q.status === "quote_sent" ? "bg-amber-100 text-amber-800" : q.status === "pending_deposit" ? "bg-blue-100 text-blue-800" : "bg-[#E6DEC3] text-[#635B52]"}`}>{q.status.replace(/_/g, " ")}</span>
          {q.expires_at && <span className="text-[#635B52] flex items-center gap-1"><Clock size={13} /> expires {q.expires_at.slice(0, 10)}</span>}
        </div>
        {q.expired && <p className="mt-3 text-red-600 text-sm">This quote has expired. Please contact us for a new one.</p>}
      </div>

      {canAccept && (
        <div className="mt-6 bg-white rounded-2xl border border-[#E6DEC3] p-6" data-testid="quote-accept">
          <p className="font-serif text-xl text-[#2C2621]">Accept & schedule</p>
          <p className="text-sm text-[#635B52] mt-1">Choose a date and time, then pay your 50% deposit. Your appointment is confirmed once payment is processed.</p>
          <label className="text-sm font-medium text-[#2C2621] mt-4 block">Date</label>
          <input type="date" min={minDate} value={date} onChange={(e) => loadSlots(e.target.value)} className={inp} data-testid="quote-date" />
          {date && (slots.length ? (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="quote-slots">
              {slots.map((s) => <button key={s} onClick={() => setTime(s)} className={`rounded-lg border py-2.5 text-sm font-medium ${time === s ? "border-[#B87333] bg-[#B87333] text-white" : "border-[#E6DEC3] text-[#2C2621]"}`} data-testid={`quote-slot-${s}`}>{s}</button>)}
            </div>
          ) : <p className="mt-3 text-sm text-[#635B52]">No times available that day.</p>)}
          <button onClick={accept} disabled={busy || !time} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3 disabled:opacity-60" data-testid="quote-accept-btn">
            <CheckCircle2 size={16} /> {busy ? "Redirecting to payment…" : `Accept & pay 50% deposit ($${(q.quote_deposit ?? Number(q.final_price) * 0.5).toFixed(2)})`}
          </button>
          <StripeSecurityNote className="mt-3" />
        </div>
      )}
      <p className="mt-6 text-xs text-[#635B52]">Your quote is based on the photos and information provided. The price may be adjusted if the property's actual condition or requested work is materially different upon arrival.</p>
    </div>
  );
}
